'use client';

import { faArrowsRotate, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, type ButtonProps } from '@repo/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
/**
 * Shared route-level error UI. Mounted by each segment's `error.tsx`
 * so a thrown render error isolates to that route instead of taking
 * down the whole authed shell (sidebar + header stay visible — only
 * the page-content slot is replaced).
 *
 * Connect0 skill §Q.9 — every page directory gets its own `error.tsx`
 * that just renders `<RouteError ... />`. Keep the wrappers tiny;
 * adjust copy/icons here, not per route.
 *
 *   // apps/web/app/(authed)/a/[accountIdOrSlug]/(settings)/settings/teams/error.tsx
 *   'use client';
 *   import { RouteError } from '@/components/route-error';
 *   export default function Error(props: { error: Error & { digest?: string }; reset: () => void }) {
 *     return <RouteError {...props} label="Teams" />;
 *   }
 *
 * The `label` is a short human title ("Teams", "Connection detail",
 * "Member invite") so the error UI says WHICH page broke without
 * the user having to guess from the URL.
 */
import { Code } from '@repo/ui/components/ui/code';
import { Disclosure } from '@repo/ui/components/ui/disclosure';
import { useEffect, useState, useTransition } from 'react';
import { isDeploySkewError, reloadOnceForSkew } from '@/lib/deploy-skew';

interface Props {
  error: Error & { digest?: string };
  reset: () => void;
  label: string;
}

/**
 * Map a raw error message into a customer-readable explanation. Covers
 * the most common shapes we surface in prod:
 *   - pg "invalid input syntax for type uuid" → user pasted a bad URL.
 *   - base58 decode failures → same root cause, surfaced via decodeUid.
 *   - control-plane errors (`account_not_found`, `forbidden`, …).
 *   - everything else falls back to the raw message.
 */
function explainError(error: Error): { headline: string; detail: string } {
  const msg = (error.message ?? '').toLowerCase();

  // Before `not found`: Next's "Server Action … was not found on the
  // server" is a page from the previous release, not a missing record.
  if (isDeploySkewError(error)) {
    return {
      headline: 'connect0 was just updated.',
      detail:
        'This page is from the previous version. Reload to continue — anything you typed in a message box is kept.',
    };
  }

  if (msg.includes('invalid input syntax for type uuid') || msg.includes('base58touuid:')) {
    return {
      headline: "We couldn't read the URL.",
      detail: "The page identifier in the URL isn't a valid id. Double-check the link or go back to the previous page.",
    };
  }
  if (msg.includes('not_found') || msg.includes('not found')) {
    return {
      headline: 'Not found.',
      detail: "We couldn't find what this page is asking for. It may have been deleted or never existed.",
    };
  }
  if (msg.includes('forbidden') || msg.includes('permission')) {
    return {
      headline: "You don't have access.",
      detail: "Your account isn't allowed to view this page. Ask an account owner to grant you access.",
    };
  }
  if (msg.includes('conflict') || msg.includes('already exists')) {
    return {
      headline: "That can't be done right now.",
      detail: 'Something on this page conflicts with the current state. Refresh and try again.',
    };
  }
  if (msg.includes('rate limit') || msg.includes('429')) {
    return {
      headline: "You're going a bit fast.",
      detail: 'We rate-limited this request. Wait a moment and try again.',
    };
  }
  if (msg.includes('econnrefused') || msg.includes('etimedout') || msg.includes('fetch failed')) {
    return {
      headline: 'Connection error.',
      detail: "We couldn't reach the backend. It's likely temporary — try again in a moment.",
    };
  }
  // Fallback: surface the raw message so support has a starting point.
  return {
    headline: 'Something went wrong.',
    detail: error.message || 'We hit an unexpected error rendering this page.',
  };
}

/**
 * "Try again" button for error boundaries. Wraps the framework `reset()`
 * in a React transition so the pending state covers the whole re-render /
 * refetch of the failed segment, and spins the rotate icon while the
 * retry is in flight (the visual "I heard you" signal).
 *
 * Deliberately NOT `<AsyncButton>`: its pending affordance is a fixed
 * leading `faCircleNotch` spinner, whereas the retry affordance here is
 * the button's own rotate icon starting to spin — the error-boundary
 * special case the shared primitive can't express. Keep this the single
 * retry button for every error surface; don't re-roll it per route.
 */
export function RetryButton({ reset, ...buttonProps }: { reset: () => void } & Omit<ButtonProps, 'onClick'>) {
  const [isPending, startTransition] = useTransition();
  return (
    <Button
      onClick={() => startTransition(() => reset())}
      disabled={isPending}
      aria-busy={isPending || undefined}
      {...buttonProps}
    >
      <FontAwesomeIcon icon={faArrowsRotate} spin={isPending} className="mr-2 h-3 w-3" />
      {isPending ? 'Retrying…' : 'Try again'}
    </Button>
  );
}

export function RouteError({ error, reset, label }: Props) {
  const [showDetails, setShowDetails] = useState(false);
  const skew = isDeploySkewError(error);
  const [reloading, setReloading] = useState(false);
  useEffect(() => {
    // A page from the previous release: load the new build once instead of
    // showing an error the user can do nothing about (lib/deploy-skew.ts).
    if (skew) {
      setReloading(reloadOnceForSkew());
      return;
    }
    console.error(`[apps/web] route error in ${label}`, error);
  }, [error, label, skew]);
  const { headline, detail } = explainError(error);

  if (skew) {
    return (
      <div className="mx-auto max-w-xl py-8">
        <Card>
          <CardHeader className="flex flex-row items-center gap-3 space-y-0">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <FontAwesomeIcon icon={faArrowsRotate} spin={reloading} className="h-4 w-4" />
            </span>
            <div className="space-y-0.5" role="status" aria-live="polite">
              <CardTitle>{reloading ? 'Loading the new version…' : headline}</CardTitle>
              <CardDescription>{detail}</CardDescription>
            </div>
          </CardHeader>
          {reloading ? null : (
            <CardContent>
              <Button size="sm" className="w-full sm:w-auto" onClick={() => window.location.reload()}>
                <FontAwesomeIcon icon={faArrowsRotate} className="mr-2 h-3 w-3" />
                Reload
              </Button>
            </CardContent>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl py-8">
      <Card>
        <CardHeader className="flex flex-row items-center gap-3 space-y-0">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-error text-text-error">
            <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4" />
          </span>
          <div className="space-y-0.5">
            <CardTitle>
              {label}: {headline}
            </CardTitle>
            <CardDescription>{detail}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <RetryButton reset={reset} size="sm" className="w-full sm:w-auto" />
          {error.digest && (
            <p className="text-xs text-muted-foreground">
              If this keeps happening, share this reference with support: <code>{error.digest}</code>
            </p>
          )}
          {error.message && error.message !== detail && (
            <Disclosure
              variant="plain"
              open={showDetails}
              onToggle={(e) => setShowDetails((e.target as HTMLDetailsElement).open)}
              summary="Technical detail"
              summaryClassName="text-xs font-normal text-muted-foreground select-none"
              contentClassName="text-xs text-muted-foreground"
            >
              <Code className="max-h-40 overflow-auto p-2">{error.message}</Code>
            </Disclosure>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
